import { useEffect, useState } from 'react';

import { getPlugins } from '../api';

/**
 * The plugins switched on anywhere (GET /plugins), shared by every component
 * that asks: the list is fetched once per page load, and again after a
 * failure. Which department a plugin is on in comes with it (`enabled`).
 */
let cache = null;
let pending = null;
const listeners = new Set();

function load() {
  if (cache || pending) return;
  pending = getPlugins()
    .then((list) => {
      cache = Array.isArray(list) ? list : [];
    })
    .catch(() => {
      cache = null;
    })
    .finally(() => {
      pending = null;
      listeners.forEach((listener) => listener(cache ?? []));
    });
}

/** Forget the list - after an administrator switched something. */
export function reloadPlugins() {
  cache = null;
  load();
}

/** @returns {Array|null} the plugins, or null while they load */
export default function usePlugins() {
  const [plugins, setPlugins] = useState(cache);

  useEffect(() => {
    listeners.add(setPlugins);
    load();
    if (cache) setPlugins(cache);
    return () => listeners.delete(setPlugins);
  }, []);

  return plugins;
}

/** Switched on in this department (null: anywhere)? The same rule as the server's. */
export function pluginEnabledIn(plugin, departmentId) {
  const { platform = false, on = [], off = [] } = plugin?.enabled ?? {};
  if (departmentId === null || departmentId === undefined) return platform || on.length > 0;
  if (off.includes(departmentId)) return false;
  if (on.includes(departmentId)) return true;
  return platform;
}

/** The plugin that draws a block of this name. */
export function pluginForBlock(plugins, name) {
  const lower = name.toLowerCase();
  return (plugins ?? []).find(
    (plugin) => plugin.block && (plugin.block.name === lower || plugin.block.aliases?.includes(lower)),
  ) ?? null;
}
